/** Shared by the real client (api.ts) and the demo adapter (demoApi.ts); kept separate to avoid an import cycle. */
import type {
  AdminDonation,
  AdminNotifyList,
  AdminRegistration,
  AdminSummary,
  AdminVolunteer,
  CheckInUpdate,
  CheckoutRequest,
  CheckoutResponse,
  Confirmation,
  CouponCheck,
  DonationCheckoutResponse,
  DonationConfirmation,
  DonationRequest,
  NotifyRequest,
  NotifySendRequest,
  NotifySendResponse,
  PublicParticipant,
  PublicStats,
  RegistrationQuery,
  VolunteerRequest,
} from '../../shared/api-types'
import type { Coupon } from '../../shared/pricing'

export const DEMO = import.meta.env.VITE_DEMO === 'true'

export interface RaceApi {
  stats(): Promise<PublicStats>
  checkCoupon(code: string): Promise<CouponCheck>
  checkout(order: CheckoutRequest): Promise<CheckoutResponse>
  confirmation(token: string): Promise<Confirmation>
  participants(q?: string): Promise<PublicParticipant[]>
  volunteer(v: VolunteerRequest): Promise<{ ok: true }>
  /** "Email me when registration opens" */
  notify(n: NotifyRequest): Promise<{ ok: true }>
  unsubscribe(token: string): Promise<{ ok: boolean }>
  donate(d: DonationRequest): Promise<DonationCheckoutResponse>
  donation(token: string): Promise<DonationConfirmation>
  /** Demo build only: simulate Stripe telling us the payment succeeded. */
  completeDemoPayment?(token: string): Promise<void>
  completeDemoDonation?(token: string): Promise<void>
  admin: {
    me(): Promise<{ authenticated: boolean }>
    login(password: string): Promise<void>
    logout(): Promise<void>
    summary(): Promise<AdminSummary>
    registrations(q: RegistrationQuery): Promise<AdminRegistration[]>
    update(id: string, patch: CheckInUpdate): Promise<AdminRegistration>
    csvUrl(): string
    coupons(): Promise<Coupon[]>
    createCoupon(c: Coupon): Promise<Coupon>
    setCouponActive(code: string, active: boolean): Promise<Coupon>
    volunteers(): Promise<AdminVolunteer[]>
    donations(): Promise<AdminDonation[]>
    donationsCsvUrl(): string
    notifyList(): Promise<AdminNotifyList>
    notifyCsvUrl(): string
    sendNotify(req: NotifySendRequest): Promise<NotifySendResponse>
  }
}

/** Errors from the API, with per-field messages when the server rejected the form. */
export class ApiError extends Error {
  status: number
  fields: Record<string, string>
  constructor(message: string, status: number, fields: Record<string, string> = {}) {
    super(message)
    this.status = status
    this.fields = fields
  }
}
